import { api } from "../scripts/api.js";

/*
   <app-reports> — the problem-report inbox.

   Built 2026-09-01 alongside the add-in's "Report a problem" button, after a
   #N/A fault in Warm Up 5 proved unreproducible and left no evidence behind.

   THE LIST IS DESIGNED AROUND ONE QUESTION: which of these is the same fault?
   A single student's report is usually not actionable; three reports on the same
   assignment in the same ten minutes almost always are. So the summary line
   leads with the two fields that identify a fault rather than a person —
   assignment and the resolved-state pair (assignmentId / userId) — and the
   student's own words come second.

   ⚠️ The payload is NOT fetched with the list. A page of 50 reports with their
   ring buffers attached would be megabytes; GET /reports omits payload and
   GET /reports/:id returns one at a time, which is why expanding a row makes a
   second call.
*/

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

function when(iso) {
   const d = new Date(iso);
   if (Number.isNaN(d.getTime())) return esc(iso);
   return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/*
   The headline verdict, computed here rather than stored, so it improves as we
   learn more failure signatures without a migration.

   The first case IS the Warm Up 5 fault: assignment resolved, userId null. That
   pair alone means GP.CHECK was throwing #N/A "Preparing assignment" on every
   cell, which is a sign-in that did not complete, not a workbook problem.
*/
function verdict(r) {
   const aid = r.state_assignment_id;
   const uid = r.state_user_id;
   if (aid && aid !== "null" && (!uid || uid === "null")) {
      return { text: "Signed in, no userId — cells would read #N/A", kind: "bad" };
   }
   if ((!aid || aid === "null") && r.assignment_code) {
      return { text: `Assignment code ${esc(r.assignment_code)} did not resolve`, kind: "bad" };
   }
   if (r.state_connected === "false") {
      return { text: "Server unreachable from the pane", kind: "warn" };
   }
   return { text: "State looks normal — read the log", kind: "" };
}

class AppReports extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <div class="card">
            <div class="row" style="justify-content:space-between;align-items:center">
               <h2 style="margin:0">Problem reports</h2>
               <select id="rp-filter">
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="all">All</option>
               </select>
            </div>
            <p class="muted" style="margin:6px 0 0">
               Filed from the add-in, either by a student pressing "Report a problem"
               or automatically when the pane detects it is stuck.
            </p>
            <div id="rp-counts" class="muted" style="margin-top:8px;font-size:13px"></div>
            <div id="rp-list" style="margin-top:12px">Loading…</div>
            <div id="rp-more" style="margin-top:8px"></div>
         </div>`;
      this.querySelector("#rp-filter").addEventListener("change", () => this.load());
      this.load();
   }

   show() { this.load(); }

   async load(append = false) {
      const list = this.querySelector("#rp-list");
      const more = this.querySelector("#rp-more");
      const status = this.querySelector("#rp-filter").value;
      const PAGE = 100;
      if (!append) {
         list.textContent = "Loading…";
         this.lastId = null;
      }
      more.innerHTML = "";
      try {
         const before = append && this.lastId ? `&before=${this.lastId}` : "";
         const res = await api(`/reports?status=${encodeURIComponent(status)}&limit=${PAGE}${before}`);
         if (!res.ok) throw new Error(String(res.status));
         const { reports, counts } = await res.json();
         if (counts) {
            this.querySelector("#rp-counts").textContent = `${counts.open} open · ${counts.resolved} resolved · ${counts.all} total`;
         }
         if (!append) list.innerHTML = "";
         if (!reports.length && !append) {
            list.innerHTML = `<p class="muted">No ${esc(status)} reports.</p>`;
            return;
         }
         list.insertAdjacentHTML("beforeend", reports.map((r) => this.rowHTML(r)).join(""));
         if (reports.length) this.lastId = reports[reports.length - 1].report_id;
         this.wireRows(list);
         if (reports.length === PAGE) {
            more.innerHTML = `<button id="rp-load-more">Load more</button>`;
            more.querySelector("button").addEventListener("click", () => this.load(true));
         }
      } catch {
         if (append) more.innerHTML = `<p class="msg err">Couldn't load reports.</p>`;
         else list.innerHTML = `<p class="msg err">Couldn't load reports</p>`;
      }
   }

   // Wire only rows not wired yet, so "Load more" doesn't double-bind the earlier ones.
   wireRows(list) {
      list.querySelectorAll("[data-expand]:not([data-wired])").forEach((btn) => {
         btn.dataset.wired = "1";
         btn.addEventListener("click", () => this.expand(btn.dataset.expand, btn));
      });
      list.querySelectorAll("[data-resolve]:not([data-wired])").forEach((btn) => {
         btn.dataset.wired = "1";
         btn.addEventListener("click", () => this.setStatus(btn.dataset.resolve, btn));
      });
   }

   rowHTML(r) {
      const v = verdict(r);
      const who = r.email ? esc(r.email) : "<em>not signed in</em>";
      const what = r.assignment_title
         ? esc(r.assignment_title)
         : r.assignment_code ? `code ${esc(r.assignment_code)}` : "no assignment";
      const auto = r.source === "auto"
         ? `<span class="pill">auto · ${esc(r.reason || "")}</span>` : "";
      const resolved = r.status === "resolved";
      const badge = resolved ? `<span class="pill rp-status">resolved</span>` : "";
      const dim = resolved && this.querySelector("#rp-filter").value === "all";
      return `
        <div class="report-row" data-row="${r.report_id}" style="border:1px solid #e5e7eb;
                  border-radius:6px;padding:10px;margin-bottom:8px;${dim ? "opacity:.6" : ""}">
          <div class="row" style="justify-content:space-between;gap:8px">
            <div>
              <strong>#${r.report_id}</strong> · ${what} · ${who} ${auto} ${badge}
              <div class="muted" style="font-size:12px">
                ${when(r.created_at)} · ${esc(r.platform || "?")} ${esc(r.app_version || "")}
                · ${esc(r.workbook_name || "")} · ${r.log_count || 0} log lines
              </div>
              <div class="verdict ${v.kind}" style="font-size:13px;margin-top:4px">${v.text}</div>
              ${r.description ? `<div style="margin-top:6px">${esc(r.description)}</div>` : ""}
            </div>
            <div style="white-space:nowrap">
              <button data-expand="${r.report_id}">Details</button>
              <button data-resolve="${r.report_id}">${r.status === "open" ? "Resolve" : "Reopen"}</button>
            </div>
          </div>
          <pre id="rp-detail-${r.report_id}" style="display:none;white-space:pre-wrap;font-size:11px;background:#f8f9fa;padding:8px;border-radius:4px;margin-top:8px;max-height:420px;overflow:auto"></pre>
        </div>`;
   }

   async expand(id, btn) {
      const pre = this.querySelector(`#rp-detail-${id}`);
      if (!pre) return;
      if (pre.style.display === "block") { pre.style.display = "none"; btn.textContent = "Details"; return; }
      pre.style.display = "block";
      btn.textContent = "Hide";
      pre.textContent = "Loading…";
      try {
         const res = await api(`/reports/${encodeURIComponent(id)}`);
         if (!res.ok) throw new Error(String(res.status));
         const { report } = await res.json();
         // textContent, never innerHTML: this is student-supplied content and a
         // log line can contain anything at all.
         pre.textContent = JSON.stringify(report.payload, null, 2);
      } catch {
         pre.textContent = "Couldn't load the payload.";
      }
   }

   async setStatus(id, btn) {
      const next = btn.textContent === "Resolve" ? "resolved" : "open";
      const row = btn.closest(".report-row");
      const filter = this.querySelector("#rp-filter").value;
      btn.disabled = true;
      row.querySelector(".rp-err")?.remove();
      try {
         const res = await api(`/reports/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify({ status: next }),
         });
         if (!res.ok) throw new Error(String(res.status));
         // No reload: a reload rebuilds the list and throws the reader back to the top.
         if (filter !== "all") {
            row.remove();
         } else {
            btn.textContent = next === "resolved" ? "Reopen" : "Resolve";
            row.style.opacity = next === "resolved" ? ".6" : "";
            const strong = row.querySelector("strong");
            const pill = row.querySelector(".pill.rp-status");
            if (next === "resolved" && !pill) strong.insertAdjacentHTML("afterend", ` <span class="pill rp-status">resolved</span>`);
            if (next === "open" && pill) pill.remove();
            btn.disabled = false;
         }
         this.bumpCounts(next);
      } catch (err) {
         btn.disabled = false;
         btn.insertAdjacentHTML("afterend",
            `<div class="msg err rp-err" style="font-size:12px">Couldn't update (${esc(err.message)}). Try again.</div>`
         );
      }
   }

   // Keep the "N open · N resolved" line right without refetching.
   bumpCounts(next) {
      const el = this.querySelector("#rp-counts");
      const m = el.textContent.match(/(\d+) open · (\d+) resolved · (\d+) total/);
      if (!m) return;
      let [open, resolved, total] = m.slice(1).map(Number);
      if (next === "resolved") {
         open--;
         resolved++;
      } else {
         open++;
         resolved--;
      }
      el.textContent = `${open} open · ${resolved} resolved · ${total} total`;
   }
}

customElements.define("app-reports", AppReports);
